import { guidePreviewRequest } from "./guide-link-preview.js";

const TOOLTIP_ATTRIBUTE = "data-fishy-tooltip";
const HEADING_ATTRIBUTE = "data-fishy-tooltip-heading";
const PLACEMENT_ATTRIBUTE = "data-fishy-tooltip-placement";
const PATH_POSITION_ATTRIBUTE = "data-fishy-tooltip-path-position";
const MODE_ATTRIBUTE = "data-fishy-tooltip-mode";
const TOOLTIP_ID = "fishystuff-site-tooltip";
const ANCHOR_ONLY_MODE = "anchor-only";
const CONTENT_LINK_SELECTOR = "[data-content-link-root] a[href]";
const PLACEMENTS = Object.freeze(["top", "right", "bottom", "left"]);
const PLACEMENT_CLASSES = Object.freeze([
    "tooltip-top",
    "tooltip-right",
    "tooltip-bottom",
    "tooltip-left",
]);
const attachedRoots = new WeakMap();

function trimString(value) {
    return String(value ?? "").trim();
}

function clamp(value, minimum, maximum) {
    if (maximum < minimum) {
        return (minimum + maximum) / 2;
    }
    return Math.min(maximum, Math.max(minimum, value));
}

function oppositePlacement(placement) {
    switch (placement) {
        case "top":
            return "bottom";
        case "right":
            return "left";
        case "bottom":
            return "top";
        case "left":
            return "right";
        default:
            return "bottom";
    }
}

function placementSpace(placement, anchorRect, viewportWidth, viewportHeight) {
    switch (placement) {
        case "top":
            return Number(anchorRect?.top ?? 0);
        case "right":
            return viewportWidth - Number(anchorRect?.right ?? 0);
        case "bottom":
            return viewportHeight - Number(anchorRect?.bottom ?? 0);
        case "left":
            return Number(anchorRect?.left ?? 0);
        default:
            return 0;
    }
}

function placementSize(placement, tooltipRect) {
    return placement === "left" || placement === "right"
        ? Number(tooltipRect?.width ?? 0)
        : Number(tooltipRect?.height ?? 0);
}

export function siteTooltipViewportPosition({
    anchorRect,
    preferredPlacement = "top",
    tooltipRect,
    viewportHeight,
    viewportWidth,
}) {
    const width = Math.max(0, Number(tooltipRect?.width ?? 0));
    const height = Math.max(0, Number(tooltipRect?.height ?? 0));
    const availableWidth = Math.max(0, Number(viewportWidth ?? 0));
    const availableHeight = Math.max(0, Number(viewportHeight ?? 0));
    const requested = PLACEMENTS.includes(preferredPlacement) ? preferredPlacement : "top";
    const candidates = [
        requested,
        oppositePlacement(requested),
        ...PLACEMENTS.filter(
            (placement) => placement !== requested && placement !== oppositePlacement(requested),
        ),
    ];
    const requiredGap = 12;
    const fitting = candidates.find((placement) => (
        placementSpace(placement, anchorRect, availableWidth, availableHeight)
        >= placementSize(placement, tooltipRect) + requiredGap
    ));
    const placement = fitting ?? candidates.reduce((best, candidate) => (
        placementSpace(candidate, anchorRect, availableWidth, availableHeight)
        > placementSpace(best, anchorRect, availableWidth, availableHeight)
            ? candidate
            : best
    ), candidates[0]);
    const margin = 12;
    const centerX = Number(anchorRect?.left ?? 0) + Number(anchorRect?.width ?? 0) / 2;
    const centerY = Number(anchorRect?.top ?? 0) + Number(anchorRect?.height ?? 0) / 2;

    if (placement === "top" || placement === "bottom") {
        return Object.freeze({
            placement,
            x: clamp(centerX, margin + width / 2, availableWidth - margin - width / 2),
            y: placement === "top"
                ? Number(anchorRect?.top ?? 0)
                : Number(anchorRect?.bottom ?? 0),
        });
    }
    return Object.freeze({
        placement,
        x: placement === "left"
            ? Number(anchorRect?.left ?? 0)
            : Number(anchorRect?.right ?? 0),
        y: clamp(centerY, margin + height / 2, availableHeight - margin - height / 2),
    });
}

function pointerPoint(event) {
    const x = Number(event?.clientX);
    const y = Number(event?.clientY);
    return Number.isFinite(x) && Number.isFinite(y) ? Object.freeze({ x, y }) : null;
}

export function siteTooltipAnchorRect(anchor, pointer = null) {
    const pathPosition = anchor?.getAttribute?.(PATH_POSITION_ATTRIBUTE);
    if (pathPosition != null) {
        const fraction = Number(pathPosition);
        if (!pathPosition.trim() || !Number.isFinite(fraction) || fraction < 0 || fraction > 1
            || typeof anchor.getPointAtLength !== "function" || typeof anchor.getScreenCTM !== "function") {
            throw new TypeError("Tooltip path position requires an SVG geometry anchor and a fraction from 0 to 1");
        }
        const transform = anchor.getScreenCTM();
        if (!transform) return null;
        const point = anchor.getPointAtLength(anchor.getTotalLength() * fraction);
        const x = transform.a * point.x + transform.c * point.y + transform.e;
        const y = transform.b * point.x + transform.d * point.y + transform.f;
        return { left: x, right: x, top: y, bottom: y, width: 0, height: 0 };
    }
    if (pointer && typeof anchor?.getClientRects === "function") {
        for (const rect of anchor.getClientRects()) {
            if (
                Number(rect?.width ?? 0) > 0
                && Number(rect?.height ?? 0) > 0
                && pointer.x >= Number(rect.left)
                && pointer.x <= Number(rect.right)
                && pointer.y >= Number(rect.top)
                && pointer.y <= Number(rect.bottom)
            ) {
                return rect;
            }
        }
    }
    return anchor?.getBoundingClientRect?.() ?? null;
}

function comparableLinkText(value) {
    let normalized = trimString(value).replace(/\s+/gu, " ");
    try {
        normalized = decodeURI(normalized);
    } catch {
        // Keep the authored representation when it contains an incomplete escape.
    }
    return normalized.endsWith("/") ? normalized.slice(0, -1) : normalized;
}

export function contentLinkTooltipText(anchor, locationHref) {
    const explicit = anchor?.getAttribute?.(TOOLTIP_ATTRIBUTE);
    if (explicit !== null && explicit !== undefined) {
        return trimString(explicit);
    }
    const rawHref = trimString(anchor?.getAttribute?.("href"));
    if (!rawHref || rawHref.startsWith("#")) {
        return "";
    }
    if (locationHref && guidePreviewRequest(rawHref, locationHref)) {
        return "";
    }

    let target;
    let current;
    try {
        current = locationHref ? new URL(locationHref) : null;
        target = new URL(rawHref, current ?? "https://fishystuff.invalid/");
    } catch {
        return "";
    }

    let destination;
    if (target.protocol === "http:" || target.protocol === "https:") {
        destination = current && target.origin === current.origin
            ? `${target.pathname}${target.search}${target.hash}`
            : target.href;
    } else if (target.protocol === "mailto:" || target.protocol === "tel:") {
        destination = rawHref;
    } else {
        return "";
    }

    const visibleText = comparableLinkText(anchor?.textContent);
    const visibleDestinations = [
        destination,
        target.href,
        destination.replace(/^(?:mailto:|tel:)/u, ""),
    ].map(comparableLinkText);
    const destinationIsVisible = visibleText && visibleDestinations.includes(visibleText);
    return destinationIsVisible ? "" : destination;
}

function tooltipTargetFromEvent(target, locationHref) {
    const explicit = target?.closest?.(`[${TOOLTIP_ATTRIBUTE}]`) ?? null;
    if (trimString(explicit?.getAttribute?.(TOOLTIP_ATTRIBUTE))) {
        return explicit;
    }
    const contentLink = target?.closest?.(CONTENT_LINK_SELECTOR) ?? null;
    return contentLinkTooltipText(contentLink, locationHref) ? contentLink : null;
}

function targetContains(container, target) {
    return Boolean(container && target && (
        container === target || container.contains?.(target)
    ));
}

function tooltipHost(anchor, documentRef) {
    let candidate = anchor;
    while (candidate && candidate !== documentRef?.body) {
        const tagName = trimString(candidate.tagName).toLowerCase();
        if (tagName === "dialog" && candidate.open) {
            return candidate;
        }
        if (candidate.hasAttribute?.("popover")) {
            return candidate;
        }
        candidate = candidate.parentElement ?? candidate.parentNode ?? null;
    }
    return documentRef?.body ?? null;
}

function ensureTooltip(documentRef) {
    const existing = documentRef?.getElementById?.(TOOLTIP_ID);
    if (!existing) {
        return null;
    }
    return {
        content: existing.querySelector?.(".tooltip-content") ?? null,
        plainText: existing.querySelector?.("[data-site-tooltip-text]") ?? null,
        tooltip: existing,
    };
}

function preferredPlacement(anchor) {
    const value = trimString(anchor?.getAttribute?.(PLACEMENT_ATTRIBUTE));
    return PLACEMENTS.includes(value) ? value : "top";
}

function tooltipMode(anchor) {
    return trimString(anchor?.getAttribute?.(MODE_ATTRIBUTE)) === ANCHOR_ONLY_MODE
        || anchor?.matches?.('button, [role="button"], input[type="button"], input[type="submit"], input[type="reset"]')
        ? ANCHOR_ONLY_MODE
        : "hoverable";
}

function focusShouldOpenTooltip(anchor) {
    if (tooltipMode(anchor) !== ANCHOR_ONLY_MODE) {
        return true;
    }
    try {
        return anchor?.matches?.(":focus-visible") !== false;
    } catch {
        return true;
    }
}

export function attachSiteTooltip(root, {
    closeDelayMs = 120,
    documentRef = globalThis.document,
    windowRef = globalThis.window,
} = {}) {
    if (!root || typeof root.addEventListener !== "function") {
        return () => {};
    }
    const existing = attachedRoots.get(root);
    if (existing) {
        return existing;
    }
    const tooltipData = ensureTooltip(documentRef);
    if (
        !tooltipData?.tooltip
        || !tooltipData.content
        || !tooltipData.plainText
    ) {
        return () => {};
    }
    const { content, plainText, tooltip } = tooltipData;
    const locationHref = windowRef?.location?.href
        ?? documentRef?.location?.href
        ?? globalThis.location?.href;
    let activeAnchor = null;
    let dismissedAnchor = null;
    let focusAnchor = null;
    let pointerAnchor = null;
    let overlayHovered = false;
    let activePointer = null;
    let closeTimer = null;
    let positionFrame = null;
    let projectedHeading = null;
    let projectedText = null;
    let projectedColor = null;

    const projectText = (anchor, text) => {
        const heading = trimString(anchor?.getAttribute?.(HEADING_ATTRIBUTE));
        const rawColor = anchor?.getAttribute?.("data-fishy-tooltip-color");
        const color = /^#[0-9a-f]{8}$/i.test(rawColor || "") ? rawColor : "";
        if (heading === projectedHeading && text === projectedText && color === projectedColor) {
            return;
        }
        projectedColor = color;
        plainText.style.color = color;
        projectedHeading = heading;
        projectedText = text;
        plainText.textContent = "";
        if (!heading) {
            plainText.textContent = text;
            return;
        }
        const title = documentRef.createElement("strong");
        title.className = "block text-center font-bold";
        title.textContent = heading;
        const body = documentRef.createElement("span");
        body.className = "block";
        body.textContent = text;
        plainText.appendChild(title);
        plainText.appendChild(body);
    };

    const cancelClose = () => {
        if (closeTimer !== null) {
            globalThis.clearTimeout?.(closeTimer);
            closeTimer = null;
        }
    };

    const cancelPosition = () => {
        if (positionFrame !== null) {
            windowRef?.cancelAnimationFrame?.(positionFrame);
            positionFrame = null;
        }
    };

    const position = () => {
        positionFrame = null;
        if (!activeAnchor || tooltip.hidden) {
            return;
        }
        const anchorRect = siteTooltipAnchorRect(activeAnchor, activePointer);
        const tooltipRect = content.getBoundingClientRect?.();
        // Read the current fixed-position origin before changing any geometry.
        // The shell starts at inset 0; subsequent inline coordinates already
        // include the prior correction. Recompute on every pass so moving hosts,
        // dialog/popover reparenting, and scrolling cannot leave a stale offset.
        const positionedRect = tooltip.getBoundingClientRect?.();
        const offsetLeft = Number(positionedRect?.left ?? 0)
            - (Number.parseFloat(tooltip.style.left) || 0);
        const offsetTop = Number(positionedRect?.top ?? 0)
            - (Number.parseFloat(tooltip.style.top) || 0);
        if (!anchorRect || !tooltipRect) {
            return;
        }
        const next = siteTooltipViewportPosition({
            anchorRect,
            preferredPlacement: preferredPlacement(activeAnchor),
            tooltipRect,
            viewportHeight: Number(windowRef?.innerHeight ?? 0),
            viewportWidth: Number(windowRef?.innerWidth ?? 0),
        });
        if (!tooltip.classList?.contains?.(`tooltip-${next.placement}`)) {
            tooltip.classList?.remove?.(...PLACEMENT_CLASSES);
            tooltip.classList?.add?.(`tooltip-${next.placement}`);
        }
        const anchorWidth = Math.max(0, Number(anchorRect.width ?? 0));
        const anchorHeight = Math.max(0, Number(anchorRect.height ?? 0));
        const vertical = next.placement === "top" || next.placement === "bottom";
        const targetLeft = vertical
            ? next.x - anchorWidth / 2
            : Number(anchorRect.left ?? 0);
        const targetTop = vertical
            ? Number(anchorRect.top ?? 0)
            : next.y - anchorHeight / 2;
        const left = `${targetLeft - offsetLeft}px`;
        const top = `${targetTop - offsetTop}px`;
        const width = `${anchorWidth}px`;
        const height = `${anchorHeight}px`;
        if (tooltip.style.left !== left) tooltip.style.left = left;
        if (tooltip.style.top !== top) tooltip.style.top = top;
        if (tooltip.style.width !== width) tooltip.style.width = width;
        if (tooltip.style.height !== height) tooltip.style.height = height;
    };

    const schedulePosition = () => {
        if (positionFrame !== null) {
            return;
        }
        if (typeof windowRef?.requestAnimationFrame === "function") {
            positionFrame = windowRef.requestAnimationFrame(position);
            return;
        }
        position();
    };

    const hide = ({ remember = false } = {}) => {
        cancelClose();
        cancelPosition();
        if (remember && activeAnchor) {
            dismissedAnchor = activeAnchor;
        }
        activeAnchor = null;
        activePointer = null;
        tooltip.hidden = true;
        tooltip.removeAttribute?.("data-fishy-tooltip-open");
        plainText.textContent = "";
        projectedHeading = null;
        projectedText = null;
    };

    const projectMode = (anchor) => {
        // A path hint switches between exact segment points. Tweening the old
        // placement's transform would detach its arrow from the new segment.
        const transitionMethod = anchor?.hasAttribute?.(PATH_POSITION_ATTRIBUTE) ? "add" : "remove";
        content.classList?.[transitionMethod]?.("transition-opacity");
        tooltip.classList?.[transitionMethod]?.("after:transition-opacity");
        const mode = tooltipMode(anchor);
        if (tooltip.getAttribute?.(MODE_ATTRIBUTE) === mode) {
            return mode;
        }
        content.classList?.remove?.("pointer-events-auto", "pointer-events-none");
        content.classList?.add?.(
            mode === ANCHOR_ONLY_MODE ? "pointer-events-none" : "pointer-events-auto",
        );
        content.classList?.toggle?.("select-none", mode === ANCHOR_ONLY_MODE);
        tooltip.setAttribute?.(MODE_ATTRIBUTE, mode);
        if (mode === ANCHOR_ONLY_MODE) {
            overlayHovered = false;
        }
        return mode;
    };

    const show = (anchor) => {
        const text = contentLinkTooltipText(anchor, locationHref);
        if (!text || anchor === dismissedAnchor) {
            return false;
        }
        cancelClose();
        activeAnchor = anchor;
        const host = tooltipHost(anchor, documentRef);
        if (host && tooltip.parentElement !== host) {
            host.appendChild(tooltip);
        }
        projectMode(anchor);
        projectText(anchor, text);
        if (tooltip.hidden) {
            tooltip.hidden = false;
            tooltip.setAttribute?.("data-fishy-tooltip-open", "");
        }
        schedulePosition();
        return true;
    };

    const interactionRemains = () => Boolean(
        pointerAnchor
        || focusAnchor
        || (overlayHovered && tooltipMode(activeAnchor) !== ANCHOR_ONLY_MODE)
    );

    const releaseDismissal = () => {
        if (!interactionRemains()) {
            dismissedAnchor = null;
        }
    };

    const scheduleClose = ({ immediate = false } = {}) => {
        cancelClose();
        if (interactionRemains()) {
            return;
        }
        if (immediate) {
            hide();
            releaseDismissal();
            return;
        }
        closeTimer = globalThis.setTimeout?.(() => {
            closeTimer = null;
            if (!interactionRemains()) {
                hide();
                releaseDismissal();
            }
        }, closeDelayMs) ?? null;
    };

    const onPointerOver = (event) => {
        if (event.pointerType === "touch") {
            return;
        }
        const target = tooltipTargetFromEvent(event.target, locationHref);
        if (!target) {
            return;
        }
        if (pointerAnchor !== target) {
            pointerAnchor = target;
            if (dismissedAnchor && dismissedAnchor !== target) {
                dismissedAnchor = null;
            }
        }
        activePointer = pointerPoint(event);
        show(target);
    };

    const onPointerMove = (event) => {
        if (event.pointerType === "touch" || !pointerAnchor || pointerAnchor !== activeAnchor) {
            return;
        }
        if (!targetContains(pointerAnchor, event.target)) {
            return;
        }
        const nextPointer = pointerPoint(event);
        if (
            !nextPointer
            || (nextPointer.x === activePointer?.x && nextPointer.y === activePointer?.y)
        ) {
            return;
        }
        activePointer = nextPointer;
        schedulePosition();
    };

    const onPointerOut = (event) => {
        const target = tooltipTargetFromEvent(event.target, locationHref);
        if (!target || target !== pointerAnchor || targetContains(target, event.relatedTarget)) {
            return;
        }
        pointerAnchor = null;
        scheduleClose({ immediate: tooltipMode(target) === ANCHOR_ONLY_MODE });
        releaseDismissal();
    };

    const onFocusIn = (event) => {
        const target = tooltipTargetFromEvent(event.target, locationHref);
        if (!target) {
            return;
        }
        if (!focusShouldOpenTooltip(target)) {
            return;
        }
        if (pointerAnchor !== target) {
            activePointer = null;
        }
        focusAnchor = target;
        if (dismissedAnchor && dismissedAnchor !== target) {
            dismissedAnchor = null;
        }
        show(target);
    };

    const onFocusOut = (event) => {
        const target = tooltipTargetFromEvent(event.target, locationHref);
        if (!target || target !== focusAnchor || targetContains(target, event.relatedTarget)) {
            return;
        }
        focusAnchor = null;
        scheduleClose();
        releaseDismissal();
    };

    const onKeyDown = (event) => {
        if (event.key === "Escape" && activeAnchor && !tooltip.hidden) {
            hide({ remember: true });
        }
    };

    const onOverlayPointerEnter = () => {
        if (tooltipMode(activeAnchor) === ANCHOR_ONLY_MODE) {
            return;
        }
        overlayHovered = true;
        cancelClose();
    };

    const onOverlayPointerLeave = () => {
        overlayHovered = false;
        scheduleClose();
        releaseDismissal();
    };

    const onViewportChange = () => schedulePosition();

    root.addEventListener("pointerover", onPointerOver);
    root.addEventListener("pointermove", onPointerMove);
    root.addEventListener("pointerout", onPointerOut);
    root.addEventListener("focusin", onFocusIn);
    root.addEventListener("focusout", onFocusOut);
    root.addEventListener("keydown", onKeyDown);
    content.addEventListener?.("pointerenter", onOverlayPointerEnter);
    content.addEventListener?.("pointerleave", onOverlayPointerLeave);
    windowRef?.addEventListener?.("resize", onViewportChange);
    windowRef?.addEventListener?.("scroll", onViewportChange, true);

    const MutationObserverRef = windowRef?.MutationObserver ?? globalThis.MutationObserver;
    const observer = typeof MutationObserverRef === "function"
        ? new MutationObserverRef((records) => {
            if (!activeAnchor) {
                return;
            }
            if (typeof root.contains === "function" && !root.contains(activeAnchor)) {
                focusAnchor = null;
                pointerAnchor = null;
                hide();
                return;
            }
            if (!records.some((record) => record.target === activeAnchor)) {
                return;
            }
            const nextText = contentLinkTooltipText(activeAnchor, locationHref);
            if (!nextText) {
                hide();
                return;
            }
            projectText(activeAnchor, nextText);
            projectMode(activeAnchor);
            schedulePosition();
        })
        : null;
    observer?.observe?.(root, {
        attributes: true,
        attributeFilter: [TOOLTIP_ATTRIBUTE, HEADING_ATTRIBUTE, PLACEMENT_ATTRIBUTE, MODE_ATTRIBUTE],
        childList: true,
        subtree: true,
    });

    const dispose = () => {
        hide();
        observer?.disconnect?.();
        root.removeEventListener?.("pointerover", onPointerOver);
        root.removeEventListener?.("pointermove", onPointerMove);
        root.removeEventListener?.("pointerout", onPointerOut);
        root.removeEventListener?.("focusin", onFocusIn);
        root.removeEventListener?.("focusout", onFocusOut);
        root.removeEventListener?.("keydown", onKeyDown);
        content.removeEventListener?.("pointerenter", onOverlayPointerEnter);
        content.removeEventListener?.("pointerleave", onOverlayPointerLeave);
        windowRef?.removeEventListener?.("resize", onViewportChange);
        windowRef?.removeEventListener?.("scroll", onViewportChange, true);
        tooltip.remove?.();
        attachedRoots.delete(root);
    };
    attachedRoots.set(root, dispose);
    return dispose;
}
